import { useDataFormatter } from "@/composable/data-formatter";
import { mdiInformationOutline } from "@mdi/js";
import { Show } from "@soft-db/soft-utils";
import { Combobox, FormField, SoftBadge, SoftIcon, SoftTooltip } from "@soft-db/ui";
import { PropType } from "vue";

type FormPeriod = string | number;

type Interval = [number, number];

export default defineComponent({
	name: "PeriodSelection",
	props: {
		modelValue: [Array, Number] as PropType<number | Interval[] | null>,
		label: String,
		disabled: Boolean,
		allowIntervals: Boolean,
		errors: Array as PropType<string[]>,
		defaultPeriod: {
			type: Array<string>,
			default: () => ["30s", "5m", "30m", "1h"],
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();

		const { getSecondsFromString, secondsToPrettyString } = useDataFormatter();
		const defaultPeriods = props.defaultPeriod.map(getSecondsFromString);

		const vModel = useVModel(props, "modelValue", emit);

		function periodMapper(val: string | number): number | null {
			if (typeof val === "number") return val;

			const parsedVal = getSecondsFromString(val);
			return isNaN(parsedVal) ? null : parsedVal;
		}

		function updatePeriod(val: FormPeriod | undefined) {
			if (val == null) {
				vModel.value = undefined;
			} else if (typeof val === "string" && val.includes("-") && props.allowIntervals) {
				vModel.value = generateIntervals(val);
			} else {
				vModel.value = periodMapper(val) || undefined;
			}
		}

		function endWithNumber(val: string): boolean {
			return /^.*\d+$/.test(val);
		}

		function addHourSuffix(val: string): string {
			val = val.trim();
			if (endWithNumber(val)) {
				val += "h";
			}
			return val;
		}

		function generateIntervals(val: string): Interval[] {
			const strNumArray = val.split("-");

			const numberArray = strNumArray
				.map(addHourSuffix)
				.map(periodMapper)
				.filter((val): val is number => typeof val === "number");

			if (numberArray.length === 0) return [];

			const intervalArray: Interval[] = [];

			let sum = 0;

			const aDayInSeconds = 86400;
			for (let i = 0; i < numberArray.length; i++) {
				if (i !== 0 && numberArray[i] === numberArray[0]) continue;

				let left: number;
				let right: number;

				if (i === numberArray.length - 1) {
					left = numberArray[i];
					right = numberArray[0];
				} else {
					left = numberArray[i];
					right = numberArray[i + 1];
				}

				if (left > right) {
					sum += aDayInSeconds - left + right;
				} else {
					sum += right - left;
				}

				intervalArray.push([left, right]);
			}

			if (sum > aDayInSeconds) {
				return [];
			}

			return intervalArray;
		}

		function removePeriod() {
			vModel.value = undefined;
		}

		const query = ref("");
		function selectOnBlur() {
			if (query.value === "") return;
			updatePeriod(query.value);

			query.value = "";
		}

		function generateBadges(item: number | Interval[]): JSX.Element[] {
			let text: string = "";

			if (Array.isArray(item)) {
				const intervalStringArray = item.map(
					(interval) => denString(interval[0]) + " - " + denString(interval[1])
				);
				text = intervalStringArray.join(",");
			} else {
				text = secondsToPrettyString(item);
			}

			return text.split(",").map((item2) => (
				<SoftBadge size="sm" color="primary" onClick:close={() => removePeriod()}>
					{item2}
				</SoftBadge>
			));
		}

		function denString(value: number): string {
			return value < 3600 ? "0h" + secondsToPrettyString(value) : secondsToPrettyString(value);
		}

		function onSelect(period: number | string) {
			updatePeriod(period);

			query.value = "";
		}

		return () => (
			<FormField label={props.label} errors={props.errors}>
				<Combobox
					v-model:filter={query.value}
					modelValue={vModel.value != null ? 1 : null}
					clearable
					disabled={props.disabled}
					placeholder={props.defaultPeriod.join(", ")}
					options={defaultPeriods}
					optionLabel={(p) => secondsToPrettyString(p)}
					customItem
					onSelect={({ option }) => onSelect(option)}
					onBlur={selectOnBlur}
					onClear={() => (vModel.value = null)}
				>
					{{
						selection: () => vModel.value && generateBadges(vModel.value),

						append: () => (
							<Show when={props.allowIntervals}>
								<SoftTooltip tooltip={t("form.period-selection.denInfo")}>
									<SoftIcon>{mdiInformationOutline}</SoftIcon>
								</SoftTooltip>
							</Show>
						),
					}}
				</Combobox>
			</FormField>
		);
	},
});
